import { useEditor, EditorContent } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { Underline } from "@tiptap/extension-underline";
import { TextStyle, FontSize } from "@tiptap/extension-text-style";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { useEffect } from "react";
import {
  TextB, TextItalic, TextUnderline, ListBullets, ListNumbers,
  Table as TableIcon, TextHOne, TextHTwo, Rows, Columns, Trash,
} from "@phosphor-icons/react";

const SIZES = [
  { v: "", n: "Size" }, { v: "12px", n: "12" }, { v: "14px", n: "14" }, { v: "16px", n: "16" },
  { v: "18px", n: "18" }, { v: "20px", n: "20" }, { v: "24px", n: "24" }, { v: "30px", n: "30" },
];

function Btn({ active, onClick, title, children, testid }) {
  return (
    <button type="button" data-testid={testid} title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className={`h-8 w-8 flex items-center justify-center rounded-md transition-colors ${active ? "bg-[#004182] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
      {children}
    </button>
  );
}

export default function RichTextEditor({ value = "", onChange, placeholder = "Type the content for this section…", testid = "rte" }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      FontSize,
      Table.configure({ resizable: false }),
      TableRow, TableHeader, TableCell,
    ],
    content: value || "",
    editorProps: { attributes: { class: "rte-content", "data-testid": `${testid}-content` } },
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  // keep external value in sync only when it differs materially (e.g. reset)
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML() && !editor.isFocused) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;
  const curSize = editor.getAttributes("textStyle").fontSize || "";

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-100 bg-slate-50">
        <Btn testid={`${testid}-bold`} active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold"><TextB size={16} weight="bold" /></Btn>
        <Btn testid={`${testid}-italic`} active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic"><TextItalic size={16} weight="bold" /></Btn>
        <Btn testid={`${testid}-underline`} active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline"><TextUnderline size={16} weight="bold" /></Btn>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <Btn testid={`${testid}-h1`} active={editor.isActive("heading", { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1"><TextHOne size={16} /></Btn>
        <Btn testid={`${testid}-h2`} active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2"><TextHTwo size={16} /></Btn>
        <select data-testid={`${testid}-fontsize`} value={curSize} onMouseDown={(e) => e.stopPropagation()}
          onChange={(e) => { const v = e.target.value; v ? editor.chain().focus().setFontSize(v).run() : editor.chain().focus().unsetFontSize().run(); }}
          className="h-8 rounded-md border border-slate-200 text-xs px-1.5 text-slate-600 outline-none">
          {SIZES.map((s) => <option key={s.v} value={s.v}>{s.n}</option>)}
        </select>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <Btn testid={`${testid}-bullet`} active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list"><ListBullets size={16} /></Btn>
        <Btn testid={`${testid}-ordered`} active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list"><ListNumbers size={16} /></Btn>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <Btn testid={`${testid}-table`} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table"><TableIcon size={16} /></Btn>
        {editor.isActive("table") && (
          <>
            <Btn testid={`${testid}-addrow`} onClick={() => editor.chain().focus().addRowAfter().run()} title="Add row"><Rows size={16} /></Btn>
            <Btn testid={`${testid}-addcol`} onClick={() => editor.chain().focus().addColumnAfter().run()} title="Add column"><Columns size={16} /></Btn>
            <Btn testid={`${testid}-deltable`} onClick={() => editor.chain().focus().deleteTable().run()} title="Delete table"><Trash size={16} /></Btn>
          </>
        )}
      </div>
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
}
